/// <amd-module name="Pipeline/Analytics/PassRateCardMetrics/PassRate.CardMetrics" />
import { AnalyticsExceptionType, recognizeAnalyticsException } from "Analytics/Common/Utilities/AnalyticsExceptionUtilities";
import { PassPercentSeries } from "Analytics/Shared/ChartSeries/PassPercentSeries";
import { ContextType, IReportContext, ITrendData, Period, TrendBy, TrendType } from "Analytics/Shared/Common/Types";
import { getTrendFromData } from "Analytics/Shared/Common/Utils/TrendCalculator";
import { ITrendChartData, ITrendChartReportConfiguration } from "Analytics/Shared/TrendChart/Common/Types";
import { getPercentage } from "Analytics/Shared/Utils/Number";
import { AreaChartHelper } from "Analytics/Shared/Visuals/AreaChartView/Common/AreaChartHelper";
import { AreaChartView } from "Analytics/Shared/Visuals/AreaChartView/ControllerViews/AreaChartView";
import { CardMetricsVisual } from "Analytics/Shared/Visuals/CardMetricsVisual/CardMetricsVisual";
import { PipelineReportInsights } from "Pipeline/Analytics/Common/Components/PipelineReportInsights";
import { IPipelineAnalyticsTelemetryService } from "Pipeline/Analytics/Common/Telemetry/PipelineAnalyticsTelemetryService";
import * as TelemetryConstants from "Pipeline/Analytics/Common/Telemetry/TelemetryConstants";
import { ITopFailingStageAndTaskData } from "Pipeline/Analytics/Common/Types";
import * as Resources from "Pipeline/Analytics/PassRateCardMetrics/Resources";
import { IBuildOutcomeData, PassRateMetricsSource } from "Pipeline/Analytics/PassRateCardMetrics/Sources/PassRateCardMetricsSource";
import * as React from "react";
import { ChartProps } from "VSS/Features/Charts/Contracts";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";

export interface ICardMetricsProps extends IVssComponentProperties {
    definitionId: number;
    onCardClick: (routeId: string, event?: any) => void;
    contextType: string;
    errorText?: string;
}

export interface IPassRateCardMetricsState {
    passPercentage: number | undefined;
    totalRuns: number | undefined;
    failedCount: number | undefined;
    topFailingStageAndTask: ITopFailingStageAndTaskData;
    topFailingTaskPercentage: number | undefined;
    definitionId: number;
    chartData?: ITrendChartData;
    trend?: ITrendData;
    errorText?: string;
    info?: string;
    iconName?: string;
}

export class PassRateCardMetrics extends VssComponent<ICardMetricsProps, IPassRateCardMetricsState> {
    private static readonly _pipelineOutcomeHubRouteUrl: string = "ms.vss-pipelineanalytics-web.pipeline-outcome-route";
    private _buildOutcomeDataPromise: Promise<IBuildOutcomeData>;
    private _topFailingTaskDataPromise: Promise<ITopFailingStageAndTaskData>;
    private _pipelineTrendDataPromise: Promise<ITrendChartData>;
    private _pipelineReportInsights: PipelineReportInsights;
    private _isTrendGood: boolean;

    constructor(props: ICardMetricsProps, context: IVssComponentContext) {
        super(props, context);
        this.state = { errorText: this.props.errorText, definitionId: this.props.definitionId } as Readonly<IPassRateCardMetricsState>;
        this._pipelineReportInsights = new PipelineReportInsights();
    }

    public async componentDidMount(): Promise<void> {
        super.componentDidMount();
        if (!this.props.errorText) {
            await this._refreshData();
        }
    }

    public render() {
        return (
            <CardMetricsVisual
                title={Resources.PipelinePassRate}
                dataValidationPromises={[this._buildOutcomeDataPromise, this._topFailingTaskDataPromise]}
                primaryMetric={
                    this.state.passPercentage != null ? { suffix: Resources.Percentage, value: this.state.passPercentage.toString() } : undefined
                }
                secondaryMetric={this.state.totalRuns ? { label: Resources.Runs, value: this.state.totalRuns.toString() } : undefined}
                trend={this.state.trend}
                isTrendGood={this._isTrendGood}
                insight={this._pipelineReportInsights._getTopFailingStageAndTaskDisplayName(
                    this.state.topFailingStageAndTask,
                    this.state.topFailingTaskPercentage
                )}
                onCardClick={this._onCardClick}
                errorText={this.state.errorText}
                info={this.state.info}
                iconName={this.state.iconName}
            >
                {this._verifyChartData(this.state.chartData) && (
                    <AreaChartView chartOptions={this._getChartOptions(this.state.chartData!)} />
                )}
            </CardMetricsVisual>
        );
    }

    public componentDidUpdate(prevProps: ICardMetricsProps, prevState: IPassRateCardMetricsState): void {
        if (this.props.definitionId !== this.state.definitionId) {
            this.setState(
                { definitionId: this.props.definitionId, passPercentage: undefined, totalRuns: undefined, trend: undefined, chartData: undefined },
                () => {
                    this._refreshData();
                }
            );
        }
        super.componentDidUpdate(prevProps, prevState);
    }

    private _onCardClick = (event?: any): void => {
        if (this.props.onCardClick) {
            this._publishPipelineCardClickTelemetry();
            this.props.onCardClick(PassRateCardMetrics._pipelineOutcomeHubRouteUrl, event);
        }
    };
    private _getChartOptions = (chartData: ITrendChartData): ChartProps | undefined => {
        const _areaChartHelper: AreaChartHelper = new AreaChartHelper();
        const chartSeries = new PassPercentSeries(chartData.secondaryChartData!.map(d => d.aggrValue));
        let chartProps = _areaChartHelper.getAreaChartOptions(this.context, chartData.secondaryChartData!, chartSeries, true);
        chartProps = _areaChartHelper.setGradientFill(chartProps, chartSeries.color);
        return chartProps;
    };

    private _verifyChartData = (chartData: ITrendChartData | undefined): boolean => {
        return chartData && chartData.secondaryChartData && chartData.secondaryChartData.length > 1 ? true : false;
    };

    private _publishPipelineCardClickTelemetry(): void {
        const properties: {
            [key: string]: any;
        } = {};
        properties[TelemetryConstants.PassRate] = this.state.passPercentage;
        properties[TelemetryConstants.TaskFailedRate] = this.state.topFailingTaskPercentage;
        const telemetryService = this.context.pageContext.getService<IPipelineAnalyticsTelemetryService>("IPipelineAnalyticsTelemetryService");
        telemetryService.publishEvent(TelemetryConstants.feature_PipelineAnalyticsFailureCard, properties);
    }

    private _publishPipelineCardNotClickTelemetry(): void {
        if (!this.state.errorText) {
            const properties: {
                [key: string]: any;
            } = {};
            properties[TelemetryConstants.PassRate] = this.state.passPercentage;
            properties[TelemetryConstants.TaskFailedRate] = this.state.topFailingTaskPercentage;
            const telemetryService = this.context.pageContext.getService<IPipelineAnalyticsTelemetryService>("IPipelineAnalyticsTelemetryService");
            telemetryService.publishEvent(TelemetryConstants.feature_Analytics_PipelineFailures_CardLoaded, properties);
        }
    }

    private async _refreshData(): Promise<void> {
        const reportContext = {
            contextType: ContextType.Build,
            definitionId: this.state.definitionId
        } as IReportContext;

        const defaultConfValues = {
            trendBy: TrendBy.Days,
            period: Period.Days_14
        } as ITrendChartReportConfiguration;

        const passRateMetricsSource = new PassRateMetricsSource(this.context.pageContext);
        this._buildOutcomeDataPromise = passRateMetricsSource.queryBuildOutcomeData(reportContext, defaultConfValues);
        this._topFailingTaskDataPromise = passRateMetricsSource.getTopFailingStageAndTaskData(reportContext, defaultConfValues);

        this._buildOutcomeDataPromise
            ?.then(buildOutcomeData => {
                if (buildOutcomeData.totalCount > 0) {
                    this.setState({
                        passPercentage: buildOutcomeData.passPercentage,
                        failedCount: buildOutcomeData.failedCount,
                        totalRuns: buildOutcomeData.totalCount,
                        errorText: undefined,
                        info: undefined,
                        iconName: undefined
                    });
                } else {
                    this.setState({ info: Resources.BuildsNotAvailable, iconName: "Rocket" });
                }
            })
            .catch(error => {
                this._onError(error);
            });

        this._topFailingTaskDataPromise.then(topFailingStageAndTaskData => {
            this.setState({
                topFailingStageAndTask: topFailingStageAndTaskData
            });
        });

        this._pipelineTrendDataPromise = passRateMetricsSource.queryPipelineTrendData(reportContext, defaultConfValues);
        this._pipelineTrendDataPromise.then(pipelineTrendChartData => {
            let trend = undefined;
            if (this._verifyChartData(pipelineTrendChartData)) {
                const len = pipelineTrendChartData.secondaryChartData!.length;
                const startValue = pipelineTrendChartData.secondaryChartData![0].aggrValue;
                const endValue = pipelineTrendChartData.secondaryChartData![len - 1].aggrValue;
                trend = getTrendFromData(startValue, endValue, false);
                this._isTrendGood = trend && trend.trendType == TrendType.Increased ? true : false;
            }
            this.setState({ chartData: pipelineTrendChartData, trend: trend });
        });

        Promise.all([this._buildOutcomeDataPromise, this._topFailingTaskDataPromise]).then(([buildOutcomeData, topFailingStageAndTaskData]) => {
            if (!this.state.errorText) {
                if (
                    this._pipelineReportInsights._hasStageAndTaskFailures(topFailingStageAndTaskData) ||
                    this._pipelineReportInsights._hasTaskFailureWithoutStage(topFailingStageAndTaskData)
                ) {
                    this.setState({
                        topFailingTaskPercentage: getPercentage(topFailingStageAndTaskData.stageFailureCount, topFailingStageAndTaskData.failedCount)
                    });
                } else {
                    this.setState({ topFailingTaskPercentage: undefined });
                }
                this._publishPipelineCardNotClickTelemetry();
            }
        });
    }

    private _onError(error: any) {
        let errorText: string = error.toString();
        if (recognizeAnalyticsException(error) === AnalyticsExceptionType.DataNotReady) {
            errorText = Resources.DataNotReady;
        }
        this.setState({ errorText: errorText, iconName: "ErrorBadge" });
    }
}

Components.add("passRateCardMetrics", PassRateCardMetrics);
